"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OperationEditorBody = void 0;
const tslib_1 = require("tslib");
const react_1 = tslib_1.__importStar(require("react"));
const ui_1 = require("@grafana/ui");
const css_1 = require("@emotion/css");
const OperationHeader_1 = require("./OperationHeader");
const OperationParamEditor_1 = require("./OperationParamEditor");
const uuid_1 = require("uuid");
const QueryEditor_1 = require("../../QueryEditor");
function OperationEditorBody({ provided, flash, isConflicting, highlight, index, queryModeller, onChange, onRemove, onToggle, operation, definition, query, timeRange, onRunQuery, datasource, }) {
    const theme = (0, ui_1.useTheme2)();
    const styles = getStyles(theme, isConflicting);
    const shouldFlash = useFlash(flash);
    const { current: id } = (0, react_1.useRef)((0, uuid_1.v4)());
    const onParamValueChanged = (paramIdx, value) => {
        const update = Object.assign(Object.assign({}, operation), { params: [...operation.params] });
        update.params[paramIdx] = value;
        callParamChangedThenOnChange(definition, update, index, paramIdx, onChange);
    };
    const onAddRestParam = () => {
        const update = Object.assign(Object.assign({}, operation), { params: [...operation.params, ''] });
        callParamChangedThenOnChange(definition, update, index, operation.params.length, onChange);
    };
    const onRemoveRestParam = (paramIdx) => {
        const update = Object.assign(Object.assign({}, operation), { params: [...operation.params.slice(0, paramIdx), ...operation.params.slice(paramIdx + 1)] });
        callParamChangedThenOnChange(definition, update, index, paramIdx, onChange);
    };
    // Handle adding button for rest params
    let restParam;
    if (definition.params.length > 0) {
        const lastParamDef = definition.params[definition.params.length - 1];
        if (lastParamDef.restParam) {
            restParam = renderAddRestParamButton(lastParamDef, onAddRestParam, index, operation.params.length, styles);
        }
    }
    return (react_1.default.createElement("div", Object.assign({ className: (0, css_1.cx)(styles.card, {
            [styles.cardHighlight]: shouldFlash || highlight,
            [styles.cardError]: isConflicting,
            [styles.disabled]: operation.disabled,
        }), ref: provided.innerRef }, provided.draggableProps, { "data-testid": `operations.${index}.wrapper` }),
        react_1.default.createElement(OperationHeader_1.OperationHeader, { operation: operation, dragHandleProps: provided.dragHandleProps, definition: definition, index: index, onChange: onChange, onRemove: onRemove, onToggle: onToggle, queryModeller: queryModeller }),
        react_1.default.createElement("div", { className: styles.body }, operation.params.map((param, paramIndex) => {
            const paramDef = definition.params[Math.min(definition.params.length - 1, paramIndex)];
            const Editor = (0, OperationParamEditor_1.getOperationParamEditor)(paramDef);
            return (react_1.default.createElement("div", { className: styles.paramRow, key: `${paramIndex}-1` },
                !paramDef.hideName && (react_1.default.createElement("div", { className: styles.paramName },
                    react_1.default.createElement("label", { htmlFor: (0, OperationParamEditor_1.getOperationParamId)(id, paramIndex) }, paramDef.name),
                    paramDef.description && (react_1.default.createElement(ui_1.Tooltip, { placement: "top", content: paramDef.description, theme: "info" },
                        react_1.default.createElement(ui_1.Icon, { name: "info-circle", size: "sm", className: styles.infoIcon }))))),
                react_1.default.createElement("div", { className: styles.paramValue },
                    react_1.default.createElement(QueryEditor_1.EditorStack, { gap: 0.5, direction: "row", alignItems: "center", wrap: false },
                        react_1.default.createElement(Editor, { index: paramIndex, paramDef: paramDef, value: operation.params[paramIndex], operation: operation, operationId: id, onChange: onParamValueChanged, onRunQuery: onRunQuery, query: query, datasource: datasource, timeRange: timeRange, queryModeller: queryModeller }),
                        paramDef.restParam && (operation.params.length > definition.params.length || paramDef.optional) && (react_1.default.createElement(ui_1.Button, { "data-testid": `operations.${index}.remove-rest-param`, size: "sm", fill: "text", icon: "times", variant: "secondary", title: `Remove ${paramDef.name}`, onClick: () => onRemoveRestParam(paramIndex) }))))));
        })),
        restParam,
        index < query.operations.length - 1 && (react_1.default.createElement("div", { className: styles.arrow },
            react_1.default.createElement("div", { className: styles.arrowLine }),
            react_1.default.createElement("div", { className: styles.arrowArrow })))));
}
exports.OperationEditorBody = OperationEditorBody;
const getStyles = (theme, isConflicting) => {
    return {
        cardWrapper: (0, css_1.css)({
            alignItems: 'stretch',
        }),
        error: (0, css_1.css)({
            marginBottom: theme.spacing(1),
        }),
        card: (0, css_1.css)({
            background: theme.colors.background.primary,
            border: `1px solid ${theme.colors.border.medium}`,
            cursor: 'grab',
            borderRadius: theme.shape.radius.default,
            position: 'relative',
            transition: 'all 0.5s ease-in 0s',
            height: isConflicting ? 'auto' : '100%',
        }),
        disabled: (0, css_1.css)({
            opacity: 0.5,
            transition: 'none',
        }),
        cardError: (0, css_1.css)({
            boxShadow: `0px 0px 4px 0px ${theme.colors.warning.main}`,
            border: `1px solid ${theme.colors.warning.main}`,
        }),
        cardHighlight: (0, css_1.css)({
            boxShadow: `0px 0px 4px 0px ${theme.colors.primary.border}`,
            border: `1px solid ${theme.colors.primary.border}`,
        }),
        infoIcon: (0, css_1.css)({
            marginLeft: theme.spacing(0.5),
            color: theme.colors.text.secondary,
            ':hover': {
                color: theme.colors.text.primary,
            },
        }),
        body: (0, css_1.css)({
            margin: theme.spacing(1, 1, 0.5, 1),
            display: 'table',
        }),
        paramRow: (0, css_1.css)({
            label: 'paramRow',
            display: 'table-row',
            verticalAlign: 'middle',
        }),
        paramName: (0, css_1.css)({
            display: 'table-cell',
            padding: theme.spacing(0, 1, 0, 0),
            fontSize: theme.typography.bodySmall.fontSize,
            fontWeight: theme.typography.fontWeightMedium,
            verticalAlign: 'middle',
            height: '32px',
        }),
        paramValue: (0, css_1.css)({
            label: 'paramValue',
            display: 'table-cell',
            verticalAlign: 'middle',
        }),
        restParam: (0, css_1.css)({
            padding: theme.spacing(0, 1, 1, 1),
        }),
        arrow: (0, css_1.css)({
            position: 'absolute',
            top: '0',
            right: '-18px',
            display: 'flex',
        }),
        arrowLine: (0, css_1.css)({
            height: '2px',
            width: '8px',
            backgroundColor: theme.colors.border.strong,
            position: 'relative',
            top: '14px',
        }),
        arrowArrow: (0, css_1.css)({
            width: 0,
            height: 0,
            borderTop: `5px solid transparent`,
            borderBottom: `5px solid transparent`,
            borderLeft: `7px solid ${theme.colors.border.strong}`,
            position: 'relative',
            top: '10px',
        }),
    };
};
/**
 * When flash is switched on makes sure it is switched of right away, so we just flash the highlight and then fade
 * out.
 * @param flash
 */
function useFlash(flash) {
    const [keepFlash, setKeepFlash] = (0, react_1.useState)(true);
    (0, react_1.useEffect)(() => {
        let t;
        if (flash) {
            t = setTimeout(() => {
                setKeepFlash(false);
            }, 1000);
        }
        else {
            setKeepFlash(true);
        }
        return () => clearTimeout(t);
    }, [flash]);
    return keepFlash && flash;
}
function callParamChangedThenOnChange(def, operation, operationIndex, paramIndex, onChange) {
    if (def.paramChangedHandler) {
        onChange(operationIndex, def.paramChangedHandler(paramIndex, operation, def));
    }
    else {
        onChange(operationIndex, operation);
    }
}
function renderAddRestParamButton(paramDef, onAddRestParam, operationIndex, paramIndex, styles) {
    return (react_1.default.createElement("div", { className: styles.restParam, key: `${paramIndex}-2` },
        react_1.default.createElement(ui_1.Button, { size: "sm", icon: "plus", title: `Add ${paramDef.name}`.trimEnd(), variant: "secondary", onClick: onAddRestParam, "data-testid": `operations.${operationIndex}.add-rest-param` }, paramDef.name)));
}
//# sourceMappingURL=OperationEditorBody.js.map